import { ShoppingBag } from 'lucide-react';
import type { MarketplaceItem } from '@/types/userAdmin';

interface MarketplaceItemListProps {
  items: MarketplaceItem[];
  limit?: number;
}

/**
 * MarketplaceItemList component displays marketplace items listed by a user
 * 
 * @param {MarketplaceItemListProps} props - Component properties
 * @returns {JSX.Element} Rendered marketplace item list
 */
export function MarketplaceItemList({ items, limit }: MarketplaceItemListProps) {
  const displayedItems = limit ? items.slice(0, limit) : items;
  const hasMore = limit && items.length > limit;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-yellow-100 text-yellow-800';
      case 'deleted':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-neutral-100 text-neutral-800';
    }
  };

  return (
    <div className="space-y-2">
      {displayedItems.map((item) => (
        <div
          key={item.id}
          className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200"
        >
          <ShoppingBag className="text-primary-600 mt-0.5 flex-shrink-0" size={18} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-medium text-neutral-900 truncate">{item.title}</p>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(item.status)}`}>
                {item.status}
              </span>
            </div>
            <p className="text-xs text-neutral-600">
              Listed {formatDate(item.created_at)}
            </p>
          </div>
        </div>
      ))}

      {hasMore && (
        <p className="text-sm text-center text-neutral-600 mt-2">
          and {items.length - limit!} more...
        </p>
      )}
    </div>
  );
}
